import { Component, Input, Output, EventEmitter } from '@angular/core';

const DEFAULT_ISSUE_FIELDS: string[] = ['cNotes', 'cAlert', 'cSupport', 'cAudioVideoQuality'];

interface IssueFieldGroupSelection {
  Id: number;
  Name: string;
  FieldSetIds: number[];
}

interface IssueFieldSelection {
  Id: number;
  FieldName?: string;
  DisplayName?: string;
}

@Component({
  selector: 'app-issue-field-selector',
  templateUrl: './issue-field-selector.component.html',
  styleUrls: ['./issue-field-selector.component.scss'],
})
export class IssueFieldSelectorComponent {
  @Input() multiSelectFilteredFields: IssueFieldSelection[] = [];
  @Input() visibleFields: IssueFieldSelection[] = [];
  @Input() fieldGroups: IssueFieldGroupSelection[] = [];
  @Input() defaultFields: string[] = DEFAULT_ISSUE_FIELDS;
  @Input() showRequired = true;
  @Input() unselectAllLabel = 'Reset all';

  @Output() fieldSelected = new EventEmitter<IssueFieldSelection>();
  @Output() fieldUnselected = new EventEmitter<IssueFieldSelection>();
  @Output() allFieldsUnselected = new EventEmitter<void>();

  filterText = '';

  isSystemField(field: IssueFieldSelection): boolean {
    return this.defaultFields.indexOf(field.FieldName) !== -1;
  }

  onSelect(field: IssueFieldSelection) {
    if (this.isSelected(field)) {
      return;
    }

    this.fieldSelected.emit(field);
  }

  onUnselect(field: IssueFieldSelection) {
    this.fieldUnselected.emit(field);
  }

  onUnselectAll() {
    this.allFieldsUnselected.emit();
  }

  clearFilter() {
    this.filterText = '';
  }

  get sortedFieldGroups(): IssueFieldGroupSelection[] {
    return [...this.fieldGroups]
      .sort((a, b) => a.Name.localeCompare(b.Name))
      .filter((group) => this.getGroupFields(group).length > 0 && this.isGroupVisibleByFilter(group));
  }

  get filteredStandaloneFields(): IssueFieldSelection[] {
    const groupedIds = this.getGroupedFieldIds();
    const normalizedFilter = this.getNormalizedFilter();

    return this.multiSelectFilteredFields.filter((field) => {
      if (!field || groupedIds.has(field.Id)) {
        return false;
      }

      if (!normalizedFilter) {
        return true;
      }

      return this.matchesFilter(field?.DisplayName, normalizedFilter);
    });
  }

  getGroupFields(group: IssueFieldGroupSelection): IssueFieldSelection[] {
    const fieldsMap = this.getCombinedFieldsById();
    return group?.FieldSetIds.map((fieldId) => fieldsMap.get(fieldId)).filter((field) => !!field);
  }

  getGroupVisibleFields(group: IssueFieldGroupSelection): IssueFieldSelection[] {
    const normalizedFilter = this.getNormalizedFilter();
    const fields = this.getGroupFields(group);

    if (!normalizedFilter) {
      return fields;
    }

    return fields.filter((field) => this.matchesFilter(field?.DisplayName, normalizedFilter));
  }

  isGroupExpanded(group: IssueFieldGroupSelection): boolean {
    const normalizedFilter = this.filterText.trim();
    if (normalizedFilter) {
      return true;
    }

    return !!this.groupExpandState[group.Id];
  }

  toggleGroupExpanded(group: IssueFieldGroupSelection, event: MouseEvent) {
    event.stopPropagation();
    this.groupExpandState[group.Id] = !this.groupExpandState[group.Id];
  }

  onGroupSelect(group: IssueFieldGroupSelection, event: MouseEvent) {
    event.stopPropagation();

    if (this.isGroupFullySelected(group)) {
      return;
    }

    const fieldsToSelect = this.getGroupFields(group).filter((field) => !this.isSelected(field));
    fieldsToSelect.forEach((field) => this.fieldSelected.emit(field));
  }

  isGroupFullySelected(group: IssueFieldGroupSelection): boolean {
    const fields = this.getGroupFields(group);
    return fields.every((field) => this.isSelected(field));
  }

  isSelected(field: IssueFieldSelection): boolean {
    return this.visibleFields?.some((x) => x.Id === field.Id) || false;
  }

  private groupExpandState: { [key: number]: boolean } = {};

  private isGroupVisibleByFilter(group: IssueFieldGroupSelection): boolean {
    const normalizedFilter = this.getNormalizedFilter();
    if (!normalizedFilter) {
      return true;
    }

    if (this.matchesFilter(group?.Name, normalizedFilter)) {
      return true;
    }

    return this.getGroupFields(group).some((field) => this.matchesFilter(field?.DisplayName, normalizedFilter));
  }

  private getCombinedFieldsById(): Map<number, IssueFieldSelection> {
    const fieldsMap = new Map<number, IssueFieldSelection>();
    [...this.multiSelectFilteredFields, ...this.visibleFields].forEach((field) => {
      if (!fieldsMap.has(field.Id)) {
        fieldsMap.set(field.Id, field);
      }
    });

    return fieldsMap;
  }

  private getGroupedFieldIds(): Set<number> {
    const groupedIds = new Set<number>();
    this.fieldGroups.forEach((group) => {
      group?.FieldSetIds.forEach((id) => groupedIds.add(id));
    });

    return groupedIds;
  }

  private getNormalizedFilter(): string {
    return this.filterText.trim().toLowerCase();
  }

  private matchesFilter(value: string | null | undefined, normalizedFilter: string): boolean {
    if (value == null) {
      return false;
    }
    return value.toLowerCase().includes(normalizedFilter);
  }
}
